import { Flex, Heading, Stack, StackDivider } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Option, Select } from '@main/complykit';
import { Card } from '@main/complykit/components/card';
import { Content, Footer, Header } from '@main/complykit/components/slots';
import {
  Integration_Handler_Names_Enum,
  Integration_Names_Enum,
  Integration_Service_Names_Enum,
} from '@main/graphql/types.generated';
import { translateOptional } from '@main/i18next/translate-optional';
import { generateHandlerDocumentation } from '@main/integrations';
import {
  findRegisteredIntegrationConfig,
  findRegisteredIntegrationServices,
  getRegisteredIntegrationConfigs,
  getRegisteredIntegrationServices,
} from '@main/integrations/registry/configs';
import { Promisable } from '@main/shared/types';
import { isConfigurableSchema, toError } from '@main/shared/utils';
import { errorToast, successToast } from '@main/ui';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod/v4';

import { AnyConfigRef, IntegrationHandlerConfig } from '../integration-handler-config';
import { MarkdownText } from '../markdown-text';

export type AddHandlerCardProps = {
  connectors: ConnectorOptionInput[];
  onAdd: (payload: AddHandlerPayload) => Promisable<void>;
};

type ConnectorOptionInput = {
  id: string;
  providerName: Integration_Names_Enum;
  name?: string;
};

type AddHandlerPayload = {
  connectorId: string;
  serviceName: Integration_Service_Names_Enum;
  handlerName: Integration_Handler_Names_Enum;
  config: object;
};

const formSchema = z.object({
  connectorId: z
    .string({ error: 'addHandlerCard.form.validation.connector' })
    .min(1, { message: 'addHandlerCard.form.validation.connector' }),
  serviceName: z.enum(Integration_Service_Names_Enum, {
    error: 'addHandlerCard.form.validation.service',
  }),
  handlerName: z.enum(Integration_Handler_Names_Enum, {
    error: 'addHandlerCard.form.validation.handler',
  }),
});

export const AddHandlerCard = ({ connectors, onAdd }: AddHandlerCardProps) => {
  const { t } = useTranslation('integrations');
  const { t: tShared } = useTranslation();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      connectorId: '',
    },
  });

  const connectorOptions = useConnectorOptions(connectors);
  const currentConnectorId = form.watch('connectorId');
  const currentProviderName = connectors.find(
    (connector) => connector.id === currentConnectorId,
  )?.providerName;

  const serviceNameOptions = useServiceNameOptions(currentProviderName);
  const currentServiceName = form.watch('serviceName');

  const handlerNameOptions = useHandlerNameOptions(currentProviderName, currentServiceName);
  const currentHandlerName = form.watch('handlerName');

  const config = useMemo(() => {
    if (!currentProviderName || !currentServiceName || !currentHandlerName) return;
    const integrationConfig = findRegisteredIntegrationConfig({
      providerName: currentProviderName,
      serviceName: currentServiceName,
      handlerName: currentHandlerName,
    });
    if (!integrationConfig) return;
    const description = translateOptional(
      t,
      `integrations.${currentProviderName}.services.${currentServiceName}.handlers.${currentHandlerName}.description`,
    );
    return {
      description,
      schema: integrationConfig.configSchema,
    };
  }, [currentHandlerName, currentProviderName, currentServiceName, t]);

  useEffect(() => {
    if (serviceNameOptions.length === 1) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      form.setValue('serviceName', serviceNameOptions[0]!.value);
    }
  }, [form, serviceNameOptions]);

  useEffect(() => {
    if (handlerNameOptions.length === 1) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      form.setValue('handlerName', handlerNameOptions[0]!.value);
    }
  }, [form, handlerNameOptions]);

  const configRef = useRef<AnyConfigRef>(null);

  const handleCopyDocs = async () => {
    if (!config || !isConfigurableSchema(config.schema)) return;
    if (!currentProviderName || !currentServiceName || !currentHandlerName) return;
    try {
      const payload = generateHandlerDocumentation({
        t,
        providerName: currentProviderName,
        serviceName: currentServiceName,
        handlerName: currentHandlerName,
        schema: config.schema,
      });
      await navigator.clipboard.writeText(payload);
      successToast(t('copyDocsBtn.successMsg'));
    } catch (error) {
      errorToast(t('copyDocsBtn.errorMsg'));
      datadogLogs.logger.error(
        'Failed copying integration handler config docs',
        {
          providerName: currentProviderName,
          serviceName: currentServiceName,
          handlerName: currentHandlerName,
        },
        toError(error),
      );
    }
  };

  return (
    <Card>
      <Header>
        <Heading size="sm">{t('addHandlerCard.title')}</Heading>
      </Header>

      <Content>
        <Stack spacing={4} divider={<StackDivider />}>
          <Stack spacing={4}>
            <Controller
              control={form.control}
              name="connectorId"
              render={({ field, fieldState }) => (
                <Select
                  ref={field.ref}
                  label={t('addHandlerCard.form.connectorId.label')}
                  isClearable
                  clearLabel={t('clearSelection', {
                    name: t('addHandlerCard.form.connectorId.label'),
                  })}
                  name={field.name}
                  value={field.value || null}
                  onChange={(id) => field.onChange(id ?? undefined)}
                  onBlur={field.onBlur}
                  isDisabled={form.formState.isSubmitting}
                  error={fieldState.error?.message && t(fieldState.error.message as never)}
                  placeholder={t('addHandlerCard.form.connectorId.placeholder')}
                  items={connectorOptions}
                  emptyMessage={t('addHandlerCard.form.connectorId.empty')}
                >
                  {(option) => <Option id={option.value}>{option.label}</Option>}
                </Select>
              )}
            />

            <Controller
              control={form.control}
              name="serviceName"
              render={({ field, fieldState }) => (
                <Select
                  ref={field.ref}
                  label={t('addHandlerCard.form.serviceName.label')}
                  isClearable
                  clearLabel={t('clearSelection', {
                    name: t('addHandlerCard.form.serviceName.label'),
                  })}
                  name={field.name}
                  value={field.value ?? null}
                  onChange={(id) => {
                    const option = serviceNameOptions.find((service) => service.value === id);
                    field.onChange(option?.value);
                  }}
                  onBlur={field.onBlur}
                  isDisabled={serviceNameOptions.length < 2 || form.formState.isSubmitting}
                  error={fieldState.error?.message && t(fieldState.error.message as never)}
                  placeholder={t('addHandlerCard.form.serviceName.placeholder')}
                  items={serviceNameOptions}
                  emptyMessage={tShared('pickers.noOptions')}
                >
                  {(option) => <Option id={option.value}>{option.label}</Option>}
                </Select>
              )}
            />

            <Controller
              control={form.control}
              name="handlerName"
              render={({ field, fieldState }) => (
                <Select
                  isFilterable
                  filterLabel={tShared('pickers.filter')}
                  filterPlaceholder={tShared('pickers.filterPlaceholder')}
                  ref={field.ref}
                  label={t('addHandlerCard.form.handlerName.label')}
                  isClearable
                  clearLabel={t('clearSelection', {
                    name: t('addHandlerCard.form.handlerName.label'),
                  })}
                  name={field.name}
                  value={field.value ?? null}
                  onChange={(id) => {
                    const option = handlerNameOptions.find((handler) => handler.value === id);
                    field.onChange(option?.value);
                  }}
                  onBlur={field.onBlur}
                  isDisabled={handlerNameOptions.length < 2 || form.formState.isSubmitting}
                  error={fieldState.error?.message && t(fieldState.error.message as never)}
                  placeholder={t('addHandlerCard.form.handlerName.placeholder')}
                  items={handlerNameOptions}
                  filterEmptyMessage={tShared('pickers.noMatches')}
                  emptyMessage={tShared('pickers.noOptions')}
                >
                  {(option) => <Option id={option.value}>{option.label}</Option>}
                </Select>
              )}
            />

            {config?.description && (
              <Stack>
                <MarkdownText text={config.description} />
              </Stack>
            )}
          </Stack>

          {config && isConfigurableSchema(config.schema) && currentProviderName && (
            <IntegrationHandlerConfig
              ref={configRef}
              orgIntegrationId={currentConnectorId}
              providerName={currentProviderName}
              serviceName={currentServiceName}
              handlerName={currentHandlerName}
              isDisabled={form.formState.isSubmitting}
            />
          )}
        </Stack>
      </Content>

      <Footer>
        <Flex justify="end" w="full" gap={2}>
          {config && isConfigurableSchema(config.schema) && (
            <Button
              variant="outline"
              isDisabled={form.formState.isSubmitting}
              onPress={() => void handleCopyDocs()}
            >
              {t('copyDocsBtn.label')}
            </Button>
          )}
          <Button
            isPending={form.formState.isSubmitting}
            onPress={() =>
              void form.handleSubmit(async (selector) => {
                // No configRef means no config fields - default to empty object.
                const config = configRef.current ? await configRef.current.submit() : {};
                if (config) {
                  await onAdd({ ...selector, config });
                }
              })()
            }
          >
            {t('addHandlerCard.form.submitBtn')}
          </Button>
        </Flex>
      </Footer>
    </Card>
  );
};

function useConnectorOptions(connectors: ConnectorOptionInput[]) {
  const { t } = useTranslation('integrations');

  return useMemo(() => {
    return connectors
      .filter((connector) => findRegisteredIntegrationServices(connector.providerName)?.size)
      .map((connector) => {
        const label = [
          t(`integrations.${connector.providerName}.name`),
          connector.name ? `(${connector.name})` : null,
        ]
          .filter(Boolean)
          .join(' ');
        return {
          value: connector.id,
          label,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [connectors, t]);
}

function useServiceNameOptions(providerName?: Integration_Names_Enum) {
  const { t } = useTranslation('integrations');

  return useMemo(() => {
    if (!providerName) {
      return [];
    }
    const serviceNames = Array.from(getRegisteredIntegrationServices(providerName).keys());
    return serviceNames
      .map((serviceName) => ({
        value: serviceName,
        label: t(`integrations.${providerName}.services.${serviceName}.name`, {
          defaultValue: serviceName,
        }),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [providerName, t]);
}

function useHandlerNameOptions(
  providerName?: Integration_Names_Enum,
  serviceName?: Integration_Service_Names_Enum,
) {
  const { t } = useTranslation('integrations');

  return useMemo(() => {
    if (!providerName || !serviceName) {
      return [];
    }
    return Array.from(getRegisteredIntegrationConfigs(providerName, serviceName)?.keys() ?? [])
      .map((handlerName) => ({
        value: handlerName,
        label: t(
          `integrations.${providerName}.services.${serviceName}.handlers.${handlerName}.name`,
          { defaultValue: handlerName },
        ),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [providerName, serviceName, t]);
}
